import {
  DUNGEON_COMBAT_ENGINE_VERSION,
  type DungeonActionKind,
  type DungeonCombatResources,
  type DungeonCombatState,
  type DungeonIntentKind,
} from "./dungeon-combat";
import { isValidDungeonChallenge } from "./dungeon-challenges";

export const DUNGEON_CHECKPOINT_SCHEMA_VERSION = 3 as const;

export type DungeonCheckpointStatus =
  | "ready"
  | "corrupt"
  | "upgrade-required";

export interface DungeonCheckpointInspection {
  status: DungeonCheckpointStatus;
  checkpoint: Record<string, unknown> | null;
  legacy: boolean;
}

const DIRECTIONS = new Set(["north", "east", "south", "west"]);
const PHASES = new Set(["navigation", "encounter", "loot"]);
const DIFFICULTIES = new Set(["explorer", "adventurer", "nightmare"]);
const INTENTS = new Set<DungeonIntentKind>(["attack", "guard", "channel"]);
const ACTIONS = new Set<DungeonActionKind>(["assault", "defense", "focus"]);
const ENCOUNTER_KINDS = new Set([
  "entrance",
  "monster",
  "trap",
  "cache",
  "boss",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function hasCommonCheckpointShape(value: Record<string, unknown>): boolean {
  return (
    Number.isInteger(value.seed) &&
    typeof value.currentCellId === "string" &&
    DIRECTIONS.has(String(value.heading)) &&
    PHASES.has(String(value.phase)) &&
    DIFFICULTIES.has(String(value.difficulty)) &&
    typeof value.grade === "string" &&
    Array.isArray(value.visited) &&
    value.visited.every((id) => typeof id === "string") &&
    Array.isArray(value.cleared) &&
    value.cleared.every((id) => typeof id === "string")
  );
}

function hasValidCombatShape(value: unknown): value is DungeonCombatState {
  if (!isRecord(value)) return false;
  return (
    (value.version === 1 || value.version === 2 || value.version === 3 || value.version === 4 || value.version === 5 || value.version === DUNGEON_COMBAT_ENGINE_VERSION) &&
    typeof value.encounterId === "string" &&
    ENCOUNTER_KINDS.has(String(value.encounterKind)) &&
    Number.isInteger(value.danger) &&
    Number.isInteger(value.round) &&
    Number.isInteger(value.commandSequence) &&
    ["player-ready", "victory", "defeat"].includes(String(value.status)) &&
    INTENTS.has(value.intent as DungeonIntentKind) &&
    (value.previousIntent === null || INTENTS.has(value.previousIntent as DungeonIntentKind)) &&
    Number.isInteger(value.patternSeed) &&
    Number.isInteger(value.patternCursor) &&
    (value.lastPlayerAction === null || ACTIONS.has(value.lastPlayerAction as DungeonActionKind)) &&
    isFiniteNumber(value.integrity) &&
    isFiniteNumber(value.maximumIntegrity) &&
    typeof value.opening === "boolean" &&
    isFiniteNumber(value.enemyPower) &&
    (value.reactionWindowMs === null || isFiniteNumber(value.reactionWindowMs)) &&
    isFiniteNumber(value.timeRemainingMs) &&
    isFiniteNumber(value.damageOnMistake) &&
    isFiniteNumber(value.damageOnTimeout) &&
    (value.challenge === undefined ||
      ((value.version === 2 || value.version === 3 || value.version === 4 || value.version === 5 || value.version === DUNGEON_COMBAT_ENGINE_VERSION) &&
        isValidDungeonChallenge(value.challenge, String(value.encounterKind), Number(value.danger))))
  );
}

function hasValidResourcesShape(value: unknown, currentEngine = false): value is DungeonCombatResources {
  if (!isRecord(value)) return false;
  const character = value.character;
  if (character !== undefined && (!isRecord(character) || !Number.isInteger(character.level) || Number(character.level) < 1 ||
    !isRecord(character.stats) || !["strength", "vitality", "dexterity", "intelligence"].every((key) =>
      Number.isInteger((character.stats as Record<string, unknown>)[key]) && Number((character.stats as Record<string, unknown>)[key]) >= 10 && Number((character.stats as Record<string, unknown>)[key]) <= 20))) return false;
  return (
    isFiniteNumber(value.vitality) &&
    isFiniteNumber(value.maximumVitality) &&
    isFiniteNumber(value.ward) &&
    isFiniteNumber(value.weaponBonus) &&
    isFiniteNumber(value.protectionBonus) &&
    (!currentEngine || value.protectionBonus === 0) &&
    (value.armorBonus === undefined || (Number.isInteger(value.armorBonus) && Number(value.armorBonus) >= 0)) &&
    (value.potions === undefined || (Number.isInteger(value.potions) && Number(value.potions) >= 0)) &&
    (currentEngine ? Number.isInteger(value.temporaryVitality) && Number(value.temporaryVitality) >= 0 && Number(value.temporaryVitality) <= 20
      : value.temporaryVitality === undefined || (Number.isInteger(value.temporaryVitality) && Number(value.temporaryVitality) >= 0))
  );
}

export function inspectDungeonCheckpointPayload(
  value: unknown,
): DungeonCheckpointInspection {
  if (!isRecord(value)) {
    return { status: "corrupt", checkpoint: null, legacy: false };
  }
  const schemaVersion = Number(value.schemaVersion);
  if (schemaVersion > DUNGEON_CHECKPOINT_SCHEMA_VERSION) {
    return { status: "upgrade-required", checkpoint: null, legacy: false };
  }
  if (Number(value.combatEngineVersion) > DUNGEON_COMBAT_ENGINE_VERSION) {
    return { status: "upgrade-required", checkpoint: null, legacy: false };
  }
  if (!hasCommonCheckpointShape(value)) {
    return { status: "corrupt", checkpoint: null, legacy: false };
  }
  if (schemaVersion === DUNGEON_CHECKPOINT_SCHEMA_VERSION) {
    const valid =
      (value.combatEngineVersion === 1 || value.combatEngineVersion === 2 || value.combatEngineVersion === 3 || value.combatEngineVersion === 4 || value.combatEngineVersion === 5 || value.combatEngineVersion === DUNGEON_COMBAT_ENGINE_VERSION) &&
      typeof value.runId === "string" &&
      value.runId.length > 0 &&
      (value.mazeVersion === undefined ||
        value.mazeVersion === 1 ||
        value.mazeVersion === 2) &&
      Number.isInteger(value.revision) &&
      typeof value.writerId === "string" &&
      hasValidCombatShape(value.combat) &&
      value.combat.version === value.combatEngineVersion &&
      hasValidResourcesShape(value.resources, value.combatEngineVersion === DUNGEON_COMBAT_ENGINE_VERSION);
    return valid
      ? { status: "ready", checkpoint: value, legacy: false }
      : { status: "corrupt", checkpoint: null, legacy: false };
  }
  const validLegacy =
    schemaVersion === 2 &&
    isFiniteNumber(value.health) &&
    isFiniteNumber(value.maxHealth);
  return validLegacy
    ? { status: "ready", checkpoint: value, legacy: true }
    : { status: "corrupt", checkpoint: null, legacy: false };
}

/** Upgrade the envelope, never rebuild an active encounter. A saved v1 fight
 * finishes with its original rules; the next room opts into challenge rules.
 * Idempotent and side-effect-free so restore and cross-tab readers agree. */
export function upgradeDungeonCheckpoint(checkpoint: Record<string, unknown>): Record<string, unknown> {
  const inspection = inspectDungeonCheckpointPayload(checkpoint);
  if (inspection.status !== "ready" || inspection.legacy) return checkpoint;
  if (checkpoint.combatEngineVersion === DUNGEON_COMBAT_ENGINE_VERSION) return checkpoint;
  const resources = checkpoint.combatEngineVersion === 5
    ? { ...(checkpoint.resources as DungeonCombatResources), protectionBonus: 0,
      temporaryVitality: Math.min(20, Math.max(0, (checkpoint.resources as DungeonCombatResources).temporaryVitality || 0)) }
    : convertLegacyAegis(checkpoint.resources as DungeonCombatResources);
  return {
    ...checkpoint,
    combatEngineVersion: DUNGEON_COMBAT_ENGINE_VERSION,
    combat: { ...(checkpoint.combat as DungeonCombatState), version: DUNGEON_COMBAT_ENGINE_VERSION },
    resources: { ...resources, armorBonus: resources.armorBonus ?? 0, potions: resources.potions ?? 0 },
  };
}

/** Old Aegis was healable max health. Preserve current total health while
 * treating any unspent bonus above base max as the new one-use pool. */
function convertLegacyAegis(resources: DungeonCombatResources): DungeonCombatResources {
  const oldBonus = Math.max(0, resources.protectionBonus || 0);
  const maximumVitality = Math.max(1, resources.maximumVitality - oldBonus);
  return {
    ...resources,
    vitality: Math.min(maximumVitality, Math.max(0, resources.vitality)),
    maximumVitality,
    protectionBonus: 0,
    temporaryVitality: Math.min(20, oldBonus, Math.max(0, resources.vitality - maximumVitality)),
  };
}

function numeric(
  checkpoint: Record<string, unknown>,
  key: string,
  fallback = 0,
): number {
  const value = Number(checkpoint[key]);
  return Number.isFinite(value) ? value : fallback;
}

export function migrateDungeonCheckpointV2(input: {
  checkpoint: Record<string, unknown>;
  combat: DungeonCombatState;
}): Record<string, unknown> {
  const inspection = inspectDungeonCheckpointPayload(input.checkpoint);
  if (inspection.status !== "ready" || !inspection.legacy) {
    throw new Error("Only a valid version 2 dungeon checkpoint can be migrated.");
  }
  const checkpoint = input.checkpoint;
  const maximumIntegrity = Math.max(
    1,
    numeric(checkpoint, "encounterTotalSteps", input.combat.maximumIntegrity),
  );
  const integrity = Math.min(
    maximumIntegrity,
    Math.max(
      0,
      numeric(checkpoint, "encounterStepsRemaining", input.combat.integrity),
    ),
  );
  const vitality = Math.max(0, numeric(checkpoint, "health"));
  const resources: DungeonCombatResources = convertLegacyAegis({
    vitality,
    maximumVitality: Math.max(1, numeric(checkpoint, "maxHealth", 100)),
    ward: Math.max(0, Math.min(2, numeric(checkpoint, "ward"))),
    weaponBonus: Math.max(0, numeric(checkpoint, "weaponBonus")),
    protectionBonus: Math.max(0, numeric(checkpoint, "protectionBonus")),
    armorBonus: 0,
    potions: 0,
  });
  const actionCount = Math.max(0, numeric(checkpoint, "actionsUsed"));
  const legacyCombat = { ...input.combat };
  delete legacyCombat.challenge;
  const combat: DungeonCombatState = {
    ...legacyCombat,
    intent: INTENTS.has(checkpoint.encounterIntent as DungeonIntentKind)
      ? checkpoint.encounterIntent as DungeonIntentKind
      : input.combat.intent,
    lastPlayerAction: ACTIONS.has(checkpoint.lastActionKind as DungeonActionKind)
      ? checkpoint.lastActionKind as DungeonActionKind
      : null,
    opening: Boolean(checkpoint.opening),
    enemyPower: Math.max(0, numeric(checkpoint, "enemyPower")),
    integrity,
    maximumIntegrity,
    timeRemainingMs: Math.max(
      0,
      numeric(checkpoint, "timeRemainingMs", input.combat.timeRemainingMs),
    ),
    round: Math.max(1, actionCount + 1),
    commandSequence: actionCount,
    patternCursor: actionCount,
    status: vitality <= 0
      ? "defeat"
      : integrity <= 0
        ? "victory"
        : "player-ready",
  };
  return {
    ...checkpoint,
    schemaVersion: DUNGEON_CHECKPOINT_SCHEMA_VERSION,
    mazeVersion: 1,
    combatEngineVersion: DUNGEON_COMBAT_ENGINE_VERSION,
    combat,
    resources,
    lastExchangeSummary: null,
  };
}
